import { useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { IconButton, Button, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PortalUsersRepository } from '../../repositories/portal_users_repository/portal_users_repository';

const blockUserCopy =
  "Are you sure you wish to block this user's access? They will no longer be able to view the portal once denied access. To view the portal in the future they will have to create a new account.";
const userIsPendingCopy =
  "This user's account is in a pending state and needs to be approved or denied access by Roam before you can make further changes.";
const userAlreadyBlockedCopy =
  "This user's account has already been denied access to the portal by Roam, or you have previously blocked the user's access to the portal.";

export default function BlockUserModal({ user, setShowBlockUserModal }) {
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useSelector((state) => state.user.user);

  const notApprovedUser = ['pending', 'denied'].includes(user.accountStatus);

  const modalText = (() => {
    if (user.accountStatus === 'pending') {
      return userIsPendingCopy;
    }
    if (user.accountStatus === 'denied') {
      return userAlreadyBlockedCopy;
    }
    return blockUserCopy;
  })();

  const modalTitle = (() => (notApprovedUser ? 'Unable to block access' : 'Block user access'))();

  const handleRequestBlockUser = async () => {
    setIsLoading(true);

    const payload = {
      userId: user.id,
      email: user.email,
      requestedBy: currentUser.email,
    };

    try {
      await PortalUsersRepository.requestBlockUser(payload, currentUser.token);
      setIsLoading(false);
      toast.success('The request to Block this user has been sent');
      setShowBlockUserModal(false);
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong. Please try again or contact support');
      setShowBlockUserModal(false);
    }
  };

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen
      onRequestClose={() => setShowBlockUserModal(false)}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
        },
        content: {
          position: 'relative',
          paddingBottom: '0px',
          width: '472px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '10px',
          borderRadius: '16px',
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <div className="p-[24px]">
        <div className="flex justify-between mb-[24px]">
          <h1 className="text-[24px]">{modalTitle}</h1>
          <IconButton
            disabled={isLoading}
            onClick={() => setShowBlockUserModal(false)}
            size="small"
            aria-label="edit"
          >
            <CloseIcon color="success" />
          </IconButton>
        </div>
        <p className="text-[14px] mb-[24px]">{modalText}</p>
        <div className="flex justify-end">
          <Button
            disabled={isLoading}
            sx={{ borderRadius: 8, height: '40px', paddingX: '24px' }}
            variant="text"
            color="success"
            onClick={() => setShowBlockUserModal(false)}
          >
            <Typography fontSize={14} textTransform={'none'}>
              Cancel
            </Typography>
          </Button>
          {!notApprovedUser && (
            <Button
              disabled={isLoading}
              onClick={handleRequestBlockUser}
              sx={{
                borderRadius: 8,
                height: '40px',
                backgroundColor: '#8C1D18',
                paddingX: '24px',
                ':hover': {
                  backgroundColor: '#8C1D18',
                },
              }}
              variant="contained"
            >
              <Typography fontSize={14} textTransform={'none'}>
                {isLoading ? 'Requesting...' : 'Request Block'}
              </Typography>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

BlockUserModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    accountType: PropTypes.oneOf(['user', 'admin']).isRequired,
    viewableSites: PropTypes.arrayOf(PropTypes.string).isRequired,
    accountStatus: PropTypes.oneOf(['approved', 'pending', 'denied']).isRequired,
  }).isRequired,
  setShowBlockUserModal: PropTypes.func.isRequired,
};
